import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtINR, fmtDateDMY } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { AlertTriangle, User, ListChecks } from "lucide-react";

export default function Flags() {
  const [tab, setTab] = useState("list"); // "list" | "by_person"
  return (
    <div className="space-y-4" data-testid="flags-root">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Red Flags</h1>
          <p className="text-sm text-slate-600 mt-1">Sorted by rupee impact. Manage big money first.</p>
        </div>
        <div className="flex gap-1 border-2 border-slate-300 rounded-sm bg-white p-0.5">
          <button data-testid="flags-tab-list" onClick={() => setTab("list")}
                  className={"px-3 py-1.5 text-xs font-bold rounded-sm flex items-center gap-1 " + (tab === "list" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")}>
            <ListChecks size={12} /> All flags
          </button>
          <button data-testid="flags-tab-by-person" onClick={() => setTab("by_person")}
                  className={"px-3 py-1.5 text-xs font-bold rounded-sm flex items-center gap-1 " + (tab === "by_person" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")}>
            <User size={12} /> By person
          </button>
        </div>
      </div>

      {tab === "list" ? <FlagList /> : <FlagsByPerson />}
    </div>
  );
}

function FlagList() {
  const [flags, setFlags] = useState([]);
  const [status, setStatus] = useState("OPEN");
  const [type, setType] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/flags/", { params: { status: status || undefined, type: type || undefined } });
      setFlags(r.data || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [status, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const ack = async (id) => { setBusy(true); try { await api.post(`/flags/${id}/ack`); load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const resolve = async (id) => {
    const note = prompt("Resolution note?");
    if (!note) return;
    setBusy(true);
    try { await api.post(`/flags/${id}/resolve`, { resolution_note: note }); load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 border border-slate-300 rounded-sm px-2 bg-white" data-testid="filter-status">
          <option value="OPEN">Open</option>
          <option value="ACK">Acknowledged</option>
          <option value="RESOLVED">Resolved</option>
          <option value="">All</option>
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} className="h-10 border border-slate-300 rounded-sm px-2 bg-white" data-testid="filter-type">
          <option value="">All types</option>
          {["NO_PROOF","UNREADABLE","MISPLACED","AMOUNT_MISMATCH","READING_MISMATCH","INCOMPLETE_READINGS","OPENING_GAP","MISSING_ENTRY","DUPLICATE_ENTRY","MISSED_ITEM_HINT","DEPOSIT_LATE","SHORT_DEPOSIT","TRIAL_ANOMALY","ADJUSTMENT_ABUSE","SHORTAGE_TIER","SALES_DROP","STOCK_NEGATIVE","PRICE_MISMATCH","CASH_VARIANCE"].map((t) => <option key={t}>{t}</option>)}
        </select>
      </div>

      {flags.length === 0 && <Card className="p-8 text-center border-2 border-slate-300 border-dashed rounded-sm">No flags.</Card>}

      <div className="space-y-2">
        {flags.map((f) => (
          <Card key={f.flag_id} data-testid={`flag-item-${f.flag_id}`} className="p-3 border-2 border-slate-300 rounded-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={14} className={f.severity === "HIGH" ? "text-red-600" : f.severity === "MED" ? "text-amber-600" : "text-slate-500"} />
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{f.type}</span>
                  <span className={"text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border " + (f.severity === "HIGH" ? "state-danger" : f.severity === "MED" ? "state-warn" : "state-info")}>{f.severity}</span>
                  <span className={"text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border " + (f.status === "OPEN" ? "state-danger" : f.status === "ACK" ? "state-warn" : "state-ok")}>{f.status}</span>
                </div>
                <div className="text-sm text-slate-900 mt-1">{f.message}</div>
                <div className="text-xs text-slate-500 mt-1">
                  <b>{f.actor_ref}</b> · {fmtDateDMY(f.date)} {f.shift_code} · {f.entity_type}:{f.entity_id}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="num text-xl font-bold text-red-600">{fmtINR(f.rupee_impact)}</div>
                <div className="flex gap-1 mt-2">
                  {f.status === "OPEN" && <Button size="sm" variant="outline" onClick={() => ack(f.flag_id)} disabled={busy} className="text-xs h-7 border-slate-300">Ack</Button>}
                  {f.status !== "RESOLVED" && <Button size="sm" onClick={() => resolve(f.flag_id)} disabled={busy} className="text-xs h-7 bg-emerald-700 hover:bg-emerald-800 text-white">Resolve</Button>}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Phase-3 B2 — Red Flags per person. 7 / 30 / 90-day windows, sorted by ₹
// impact so the manager knows who's costing money. Cash variance ("Misc
// bucket") flags naturally show up in `by_type.CASH_VARIANCE` since A1
// stores them in the same flags collection.
function FlagsByPerson() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState({ days: 30, people: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBusy(true);
    api.get("/flags/by_person", { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(false));
  }, [days]);

  const windows = [7, 30, 90];
  return (
    <div className="space-y-3">
      <div className="flex gap-1 border-2 border-slate-300 rounded-sm bg-white p-0.5 w-fit" data-testid="by-person-windows">
        {windows.map((w) => (
          <button key={w} onClick={() => setDays(w)}
                  data-testid={`by-person-window-${w}`}
                  className={"px-3 py-1.5 text-xs font-bold rounded-sm " + (days === w ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")}>
            Last {w} days
          </button>
        ))}
      </div>

      {busy && <div className="text-xs text-slate-500">Loading…</div>}
      {(!busy && data.people.length === 0) && <Card className="p-8 text-center border-2 border-slate-300 border-dashed rounded-sm">No flags attributed to any person in the last {days} days.</Card>}

      <div className="space-y-2">
        {data.people.map((p) => {
          const misc = p.by_type?.CASH_VARIANCE || 0;
          return (
            <Card key={p.person} className="p-3 border-2 border-slate-300 rounded-sm" data-testid={`person-row-${p.person}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <User size={14} className="text-slate-700" />
                    <span className="font-bold text-slate-900">{p.person}</span>
                    <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{p.count} flag{p.count === 1 ? "" : "s"}</span>
                    {p.high > 0 && <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-sm border state-danger">{p.high} HIGH</span>}
                    {p.med > 0 && <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-sm border state-warn">{p.med} MED</span>}
                    {p.low > 0 && <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-sm border state-info">{p.low} LOW</span>}
                    {misc > 0 && (
                      <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-sm border border-amber-400 bg-amber-50 text-amber-900" data-testid={`person-misc-${p.person}`}>
                        Misc/variance × {misc}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {Object.entries(p.by_type || {}).map(([t, c]) => (
                      <span key={t} className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-sm border border-slate-300 bg-slate-50 text-slate-700">
                        {t} × {c}
                      </span>
                    ))}
                  </div>
                  {(p.recent || []).length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-800">Recent {p.recent.length} flag{p.recent.length === 1 ? "" : "s"}</summary>
                      <ul className="mt-1 space-y-0.5">
                        {p.recent.map((r) => (
                          <li key={r.flag_id} className="text-[11px] text-slate-600">
                            <b>{r.type}</b> · {fmtDateDMY(r.date)} {r.shift_code} · <span className="text-red-700 font-mono">{fmtINR(r.impact)}</span> — {r.message}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Total impact</div>
                  <div className="num text-2xl font-bold text-red-600">{fmtINR(p.total_impact)}</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
